import { useEffect, useRef } from 'react';
import useEditorStore from '../../stores/useEditorStore';

const LOG_PREFIX = '[LoadingOverlay]';
const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** How long the overlay may stay up before it is reported as stuck. */
const STALL_WARN_MS = 15000;
const STALL_REPEAT_MS = 15000;

export default function LoadingOverlay({ progress = null, showBackdrop = true }) {
  const mountT0Ref = useRef(nowMs());
  const hasProgress = typeof progress === 'number' && !Number.isNaN(progress);
  const pct = hasProgress ? Math.max(0, Math.min(100, progress)) : 0;

  useEffect(() => {
    const t0 = mountT0Ref.current;
    const { describeLoadingOwners } = useEditorStore.getState();
    console.log(`${LOG_PREFIX} MOUNT visible`, {
      atMs: t0.toFixed(1),
      ...describeLoadingOwners(),
    });

    // A "loading forever" overlay is otherwise silent — the operation that
    // raised it is long gone from the log by the time anyone notices. Name the
    // holder on a timer instead of leaving it to be guessed at.
    const stall = setInterval(() => {
      console.warn(`${LOG_PREFIX} STILL VISIBLE — possible stuck overlay`, {
        visibleForMs: Math.round(nowMs() - t0),
        ...useEditorStore.getState().describeLoadingOwners(),
      });
    }, STALL_REPEAT_MS);
    const firstStall = setTimeout(() => {
      console.warn(`${LOG_PREFIX} STILL VISIBLE — possible stuck overlay`, {
        visibleForMs: Math.round(nowMs() - t0),
        ...useEditorStore.getState().describeLoadingOwners(),
      });
    }, STALL_WARN_MS);

    return () => {
      clearInterval(stall);
      clearTimeout(firstStall);
      console.log(`${LOG_PREFIX} UNMOUNT hidden`, {
        visibleForMs: (nowMs() - t0).toFixed(1),
      });
    };
  }, []);

  return (
    <>
      {showBackdrop && (
        <div className="absolute inset-0 left-0 top-0 z-[1000] h-screen w-screen bg-white/15 backdrop-blur-[3px] transition-[backdrop-filter] duration-300 [-webkit-backdrop-filter:blur(3px)]" />
      )}

      <div className="pointer-events-none absolute inset-0 z-[1001] flex items-center justify-center">
        <div className="flex w-64 flex-col items-center gap-4">
          <div
            role="status"
            aria-label="Loading"
            className="odp-loader-spin size-12 rounded-full border-4 border-gray-300 border-t-[var(--odp-purple-light)]"
          />
          <p className="text-center text-sm font-medium text-gray-700">Loading</p>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-300">
            <div
              className="h-full rounded-full bg-[var(--odp-purple-light)] transition-[width] duration-300 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
